// ─────────────────────────────────────────────────────────────────────────────
// Voice Mode 2.0 — Phase P0 "Visible Spine": React bindings for the UI control
// runtime.  Mounted once from VirtualAssistantProvider (spec §9: SSE subscribe
// + acks) alongside UiControlLayer.
//
// `useUiControlBootstrap` wires the runtime to the router (navigate) and the
// active workspace, connects when a session token exists, and disconnects on
// unmount.  When the server flag `voice_ui_control` is OFF (the default) the
// runtime stays idle: no SSE connection, no overlay, zero behaviour change.
// `useUiControlEvents` exposes the immutable state snapshot to components
// (useSyncExternalStore — referentially stable).
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useSyncExternalStore } from 'react';

import { getUiControlRuntime, UiRuntimeState } from './runtime';

/** Read the runtime state snapshot (highlight ring, captions, phase…). */
export function useUiControlEvents(): UiRuntimeState {
  const runtime = getUiControlRuntime();
  return useSyncExternalStore(runtime.subscribe, runtime.getSnapshot, runtime.getSnapshot);
}

interface BootstrapOptions {
  /** react-router navigate (kept in a ref; reconnects never re-wire). */
  navigate?: (to: string) => void;
  /** Active workspace id provider (localStorage-backed in this app). */
  getWorkspaceId?: () => string | null;
}

/** Connect/disconnect the runtime for the lifetime of the mounting provider. */
export function useUiControlBootstrap(options: BootstrapOptions = {}): void {
  const runtime = getUiControlRuntime();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!localStorage.getItem('saik_token')) return; // not signed in
    runtime.init({
      navigate: (to: string) => optionsRef.current.navigate?.(to),
      getWorkspaceId: () =>
        optionsRef.current.getWorkspaceId?.() ??
        localStorage.getItem('saik_workspace_id'),
    });
    void runtime.connect();
    return () => {
      runtime.disconnect();
    };
  }, [runtime]);
}

export default useUiControlEvents;